import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    OnDestroy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    CustomFieldControl,
    DataService,
    SharedModule,
    AssetFragment,
} from '@vendure/admin-ui/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { GetAllProductCustomizationOptionListDocument, getProductCustomizationOptionDocument } from '../common/graphql';

interface DisplayOption {
    id: string;
    name: string;
    type: string;
    previewAsset?: AssetFragment | null;
}

export const CUSTOM_OPTION_TYPES = [
    { value: 'custom-print-template', label: 'Print Template' },
    { value: 'custom-message',        label: 'Message'        },
    { value: 'custom-options',         label: 'Options (Single-Select)' },
    { value: 'custom-text-frames',     label: 'Text Overlay'            },
] as const;

@Component({
    selector: 'product-customization-option-selector',
    standalone: true,
    imports: [SharedModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <vdr-card class="mb-2">
    <div class="flex items-center mb-2" *ngIf="selectedOptionDisplay as opt">
        <ng-container *ngIf="opt?.type === 'custom-print-template'">
        <div *ngIf="opt.previewAsset as asset; else imagePlaceholder">
            <img class="image-placeholder w-12 h-12 object-cover rounded border border-gray-200"
                [src]="asset | assetPreview : 'tiny'"
            />
        </div>
        <ng-template #imagePlaceholder>
            <div class="placeholder w-12 h-12 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                <clr-icon shape="image" size="24" class="text-gray-400"></clr-icon>
            </div>
        </ng-template>
        </ng-container>

        <ng-container *ngIf="opt?.type !== 'custom-print-template'">
            <div class="placeholder w-12 h-12 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                <clr-icon [attr.shape]="getIconShape(opt.type)" size="24" class="text-gray-400"></clr-icon>
            </div>
        </ng-container>
        <span class="truncate ml-4 flex-grow">
            <a class="button-ghost" [routerLink]="['/extensions','product-customization-options', opt.id]">{{ opt.name }}</a>
        </span>
        <span class="font-mono text-xs ml-4 mr-1 text-gray-500">type:<strong>{{ getOptionNameFromType(opt.type) }}</strong></span>
        <button
            *ngIf="!editMode && !readonly"
            type="button"
            class="vdr-button vdr-button--secondary vdr-button--sm"
            (click)="enableEdit()"
        >
            {{ 'common.change-selection' | translate }}
        </button>
    </div>

    <div *ngIf="editMode || !selectedOptionDisplay">
        <div class="flex items-center gap-2 mb-2">
            <select
                class="vdr-form-select flex-none"
                [formControl]="typeCtrl"
                [disabled]="readonly"
            >
                <option [ngValue]="null">{{ 'common.select' | translate }}</option>
                <option *ngFor="let t of types" [ngValue]="t.value">{{ t.label }}</option>
            </select>
            <input
                class="vdr-form-input flex-auto"
                [placeholder]="'common.search-by-name' | translate"
                [formControl]="searchCtrl"
                [disabled]="readonly"
            />
        </div>

        <select class="mt-2 w-full"
            [formControl]="formControl"
            [disabled]="readonly"
        >
            <option [ngValue]="null">{{ 'common.select' | translate }}</option>
            <option *ngFor="let item of (options$ | async)" [ngValue]="item.id">
                {{ getOptionNameFromType(item.type) }} · {{ item.name }}
            </option>
        </select>
    </div>
    </vdr-card>
    `,
})
export class ProductCustomizationOptionSelectorComponent implements OnInit, OnDestroy, CustomFieldControl {
    /* ——— CustomFieldControl API ——— */
    @Input() formControl!: FormControl<string | null>; // Expecting string ID or null
    @Input() readonly!: boolean;
    @Input() config: any;

    /* ——— Filter controls ——— */
    readonly types = CUSTOM_OPTION_TYPES;
    typeCtrl = new FormControl<string | null>(null);
    searchCtrl = new FormControl('');
    editMode = false;
    options$: any;

    // Holds the details of the currently selected option for display
    selectedOptionDisplay: DisplayOption | null = null;

    private baselineId: string | null = null;
    private destroy$ = new Subject<void>();
    private valueChangesSub: Subscription | null = null;

    constructor(
        private dataService: DataService,
        private changeDetector: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        // --- FIX: Ensure baselineId is string or null ---
        const initialValue = this.formControl.value;
        this.baselineId = initialValue !== null ? String(initialValue) : null;

        // Fetch display details for the initial ID if it exists
        this.updateSelectedOptionDisplay(this.baselineId);

        // When the formControl value (the ID) changes...
        this.valueChangesSub = this.formControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(currentValue => {
                const currentId = currentValue;
                this.updateSelectedOptionDisplay(currentId);

                if (currentId === this.baselineId) {
                    this.formControl.markAsPristine();
                } else {
                    this.formControl.markAsDirty();
                }
                // If an option was selected via the dropdown, exit edit mode
                if (currentId !== null) {
                    this.editMode = false;
                }
                this.changeDetector.markForCheck();
            });

        // Observable to fetch options for the dropdown based on filters
        this.options$ = this.typeCtrl.valueChanges.pipe(
            startWith(this.typeCtrl.value),
            switchMap(type => this.searchCtrl.valueChanges.pipe(
                startWith(this.searchCtrl.value),
                debounceTime(200),
                switchMap(term =>
                    this.dataService
                        .query(GetAllProductCustomizationOptionListDocument, {
                            options: {
                                take: 50,
                                filter: {
                                    name: term ? { contains: term } : undefined,
                                    type: type ? { eq: type } : undefined,
                                    enabled: { eq: true },
                                }
                            }
                        })
                        .mapSingle((data:any) => data.productCustomizationOptions.items)
                )
            )),
            takeUntil(this.destroy$),
        );
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.valueChangesSub?.unsubscribe();
    }

    /** Fetch and update the display details for the given option ID */
    private updateSelectedOptionDisplay(optionId: string | null) {
         // Ensure optionId is treated as string for comparison and fetching
         const idToFetch = optionId !== null ? String(optionId) : null;

        if (idToFetch) {
            // Fetch the full option details only if we don't already have them or ID changed
            if (!this.selectedOptionDisplay || this.selectedOptionDisplay.id !== idToFetch) {
                 this.selectedOptionDisplay = null;
                 this.changeDetector.markForCheck();

                 this.dataService.query(getProductCustomizationOptionDocument, { id: idToFetch }) // Use string ID
                    .mapSingle((data:any) => data.productCustomizationOption)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(fullOption => {
                        if (fullOption) {
                            this.selectedOptionDisplay = {
                                id: fullOption.id,
                                name: fullOption.name,
                                type: fullOption.type,
                                previewAsset: fullOption.previewAsset,
                            };
                        } else {
                            this.selectedOptionDisplay = null;
                        }
                        this.changeDetector.markForCheck();
                    });
            }
        } else {
            this.selectedOptionDisplay = null;
            this.changeDetector.markForCheck();
        }
    }

    getOptionNameFromType(type: string | null | undefined): string {
        if (!type) return '';
        return this.types.find((o) => o.value === type)?.label || type;
    }

    getIconShape(type: string | null | undefined): string {
        switch (type) {
            case 'custom-message': return 'chat-bubble';
            case 'custom-print-template': return 'image';
            case 'custom-options': return 'list';
            case 'custom-text-frames': return 'text';
            default: return 'tag';
        }
    }

    enableEdit() {
        this.editMode = true;
        this.typeCtrl.setValue(null, { emitEvent: false });
        this.searchCtrl.setValue('', { emitEvent: false });
        this.changeDetector.markForCheck();
    }
}
